import React from 'react';
import { useParams } from 'react-router-dom';
import { CalendarIcon, ClockIcon, UserIcon, ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, BriefcaseIcon, TrendingUpIcon, SearchCheckIcon } from 'lucide-react';
import { SchoolListings } from '../components/SchoolListings';
import { useSchoolSearch } from '../hooks/useSchoolSearch';
import { useGeoLocation } from '../hooks/useGeoLocation';
import { useMetaTags } from '../hooks/useMetaTags';
import { articles, Article, ArticleBlock } from '../data/articlesData';
import { areaOfStudyData } from '../data/areaOfStudyData';
import { getDefaultProgram } from '../data/filterOptions';
interface ArticleDetailPageProps {
  onNavigate: (page: string, articleSlug?: string) => void;
  onProgramChange: (programId: number) => void;
}
// Map an article's area of study to the search params that make the embedded
// listings match the article topic. mProgram uses the area's "General"
// program (area * 100); area 0 (editorial/general) falls back to 900.
function getSearchTargets(areaStudyId: number): {
  degreeInterest: number;
  mProgram: number;
} {
  const mProgram = areaStudyId >= 1 ? getDefaultProgram(areaStudyId) : 900;
  // Trades skew toward Associate's/Certificate; everything else Bachelor's.
  const degreeInterest = areaStudyId === 7 ? 2 : 3;
  return {
    degreeInterest,
    mProgram
  };
}
export function ArticleDetailPage({
  onNavigate,
  onProgramChange
}: ArticleDetailPageProps) {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const article: Article | undefined = articles.find(a => a.slug === slug);
  const area = article ? areaOfStudyData[article.areaStudyId] : undefined;
  const {
    degreeInterest,
    mProgram
  } = getSearchTargets(article?.areaStudyId ?? 0);
  const {
    geo
  } = useGeoLocation();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest,
    mProgram,
    zipCode: geo.zipCode
  });
  useMetaTags({
    title: article ? `${article.title} | FindOnlineDegrees` : 'Article Not Found | FindOnlineDegrees',
    description: article?.excerpt || 'Read expert guides for online students.',
    keywords: article ? `${article.category}, online degree, ${area?.name || ''}, ${article.title}` : 'online education articles',
    ogType: 'article',
    author: article?.author || 'FindOnlineDegrees Editorial Team'
  });
  if (!article) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-id="element-1301" data-mp="tnjvtk184sqq">
        <div className="text-center max-w-md" data-id="element-1302" data-mp="ooyybsdcwn5z">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1303" data-mp="oo77euupfdm1">
            Article not found
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-1304" data-mp="13dbq0d5evjuo">
            We couldn't find the article you were looking for.
          </p>
          <button onClick={() => onNavigate('articles')} className="inline-flex items-center gap-2 bg-[#1a2332] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#2a374a] transition-colors" data-id="element-1305" data-mp="yh4m0pwksifd">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1306" data-mp="1r10c38hslylj" />
            Browse all articles
          </button>
        </div>
      </div>;
  }
  const topic = area?.name || article.category;
  // Articles can pull the embedded school listings up into the body by
  // declaring how many blocks should appear above them.
  const blocks = article.content ?? [];
  const splitAt = article.listingsAfterBlock !== undefined ? Math.min(article.listingsAfterBlock, blocks.length) : blocks.length;
  const leadBlocks = blocks.slice(0, splitAt);
  const trailingBlocks = blocks.slice(splitAt);
  const renderBlock = (block: ArticleBlock, i: number) => {
    if (block.type === 'h2') {
      return <h2 key={i} className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1307" data-mp="13aw8h14ltyuj">
          {block.text}
        </h2>;
    }
    if (block.type === 'h3') {
      return <h3 key={i} className="text-xl font-bold text-gray-900 mt-8 mb-3" data-id="element-1308" data-mp="uazo3bp8zm5a">
          {block.text}
        </h3>;
    }
    if (block.type === 'ul') {
      return <ul key={i} className="list-disc pl-6 mb-4 text-gray-700 space-y-2" data-id="element-1309" data-mp="1wnm3ykrv7opv">
          {block.items?.map((item, j) => <li key={j} data-id="element-1310" data-mp="1nw3qwiq7tp10">{item}</li>)}
        </ul>;
    }
    return <p key={i} className="text-gray-700 leading-relaxed mb-4" data-id="element-1311" data-mp="1ntzch6uox7mh">
        {block.text}
      </p>;
  };
  const relatedArticles = articles.filter(a => a.areaStudyId === article.areaStudyId && a.slug !== article.slug).slice(0, 3);
  return <div className="min-h-screen bg-gray-50" data-id="element-1312" data-mp="18y1mk6bd0zip">
      {/* Breadcrumb / Back */}
      <div className="bg-white border-b border-gray-200" data-id="element-1313" data-mp="14wg1vysl3gxy">
        <div className="max-w-4xl mx-auto px-4 py-4" data-id="element-1314" data-mp="14z2jkr96vctw">
          <button onClick={() => onNavigate('articles')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#3b82f6] transition-colors" data-id="element-1315" data-mp="zz6cgj5735a4">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-1316" data-mp="2ud7d99kb22cu" />
            Back to Articles
          </button>
        </div>
      </div>

      {/* Article Hero */}
      <article className="bg-white" data-id="element-1317" data-mp="31tklz6pkwy9r">
        <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-1318" data-mp="36u8gw3gxhz99">
          <div className="inline-block bg-blue-50 text-[#3b82f6] px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-1319" data-mp="36sourw8lctzk">
            {article.category}
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1320" data-mp="3gj9tdjo9lyih">
            {article.title}
          </h1>

          <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-1321" data-mp="2nvw324f2o17c">
            {article.excerpt || `A complete guide to ${topic.toLowerCase()} for online students in 2026.`}
          </p>

          {article.heroImage && <img src={article.heroImage} alt={article.title} className="w-full rounded-2xl mb-8 object-cover" data-id="element-1322" data-mp="2wxn6t3q1xxyk" />}

          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 pb-8 border-b border-gray-200" data-id="element-1323" data-mp="c5tryjuklxav">
            <div className="flex items-center gap-2" data-id="element-1324" data-mp="2967o5crdnht8">
              <UserIcon className="w-4 h-4" data-id="element-1325" data-mp="298tzlnhe2hgq" />
              <span data-id="element-1326" data-mp="246uti5u9l6g3">{article.author}</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1327" data-mp="2i7p3gswynfgv">
              <CalendarIcon className="w-4 h-4" data-id="element-1328" data-mp="2i6dxqnjyfxn4" />
              <span data-id="element-1329" data-mp="2d8kti027pdph">{article.date}</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1330" data-mp="2d945z193f9w4">
              <ClockIcon className="w-4 h-4" data-id="element-1331" data-mp="1pii8g3wpt80d" />
              <span data-id="element-1332" data-mp="1pj9rygd1ffaa">{article.readTime} read</span>
            </div>
          </div>
        </div>
      </article>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-1333" data-mp="1z8jktyfph1zg">
        <div className="prose prose-lg max-w-none" data-id="element-1334" data-mp="1zae8hkid3878">
          {/* Rich, hand-published body (when the article ships full content) */}
          {leadBlocks.length > 0 && <div className="mb-12" data-id="element-1335" data-mp="16rgyylxyxu2k">{leadBlocks.map(renderBlock)}</div>}

          {/* Auto-generated, area-based body (short articles without content) */}
          {!article.content && <>
              {/* Introduction */}
              <section className="mb-12" data-id="element-1336" data-mp="3dey6btu5xio7">
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1337" data-mp="3i8j3xkrugamv">
                  {area?.description || `Choosing the right online program is one of the most important decisions you'll make for your career.`}{' '}
                  This guide breaks down what you need to know about{' '}
                  {topic.toLowerCase()} — from career outcomes and earning
                  potential to what makes a program worth your time and money.
                </p>
                <p className="text-gray-700 leading-relaxed" data-id="element-1338" data-mp="3n73wqz4rns1a">
                  We've pulled together the latest data and practical advice so
                  you can compare accredited online options with confidence and
                  find the path that fits your goals.
                </p>
              </section>

              {/* Career outlook */}
              {area && <section className="mb-12" data-id="element-1339" data-mp="38dtir8ydhaqp">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1340" data-mp="38euwxmaoqdcn">
                    <TrendingUpIcon className="w-6 h-6 text-[#e8594f]" data-id="element-1341" data-mp="33e718nbih8c3" />
                    Career Outlook &amp; Salary
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1342" data-mp="2tgu2kwpgg9km">
                    {area.careerOutlook}
                  </p>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6" data-id="element-1343" data-mp="3r4ug5sjugy85">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1344" data-mp="3m4ya7nrl6wpy">
                      Typical Salary Range
                    </p>
                    <p className="text-2xl font-bold text-gray-900" data-id="element-1345" data-mp="3hcewmqswg1l5">
                      {area.salaryRange}
                    </p>
                  </div>
                </section>}

              {/* Common career paths */}
              {area?.jobTitles?.length ? <section className="mb-12" data-id="element-1346" data-mp="8xn41h0on738">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1347" data-mp="h83x3phyytj3">
                    <BriefcaseIcon className="w-6 h-6 text-[#e8594f]" data-id="element-1348" data-mp="l8nt2pqt3r3n" />
                    Common Career Paths
                  </h2>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 list-none pl-0" data-id="element-1349" data-mp="q7ioqs9vha56">
                    {area.jobTitles.map(title => <li key={title} className="flex items-center gap-2.5 text-gray-700" data-id="element-1350" data-mp="106g8lhwcsfhu">
                        <CheckCircleIcon className="w-5 h-5 text-[#3b82f6] flex-shrink-0" data-id="element-1351" data-mp="146ic7wlnfizr" />
                        <span data-id="element-1352" data-mp="1903374otrcpg">{title}</span>
                      </li>)}
                  </ul>
                </section> : null}

              {/* Why study */}
              {area?.whyStudy && <section className="mb-12" data-id="element-1353" data-mp="1iv4otqjw5wna">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1354" data-mp="1nv9aduznphkd">
                    Why Study {topic}?
                  </h2>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1355" data-mp="t7dkdv6lqi6y">
                    {area.whyStudy}
                  </p>
                </section>}
            </>}

          {/* Embedded School Listings */}
          <section className="mb-12" data-id="element-1356" data-mp="16wxi2mlbsp9e">
            <div className="bg-blue-50 border-l-4 border-[#3b82f6] p-6 rounded-r-lg mb-8" data-id="element-1357" data-mp="16xz39jw22s4u">
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1358" data-mp="1bwtostlequsn">
                Compare Top Online {topic} Programs
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1359" data-mp="1gtac5lks954o">
                Based on the criteria discussed in this article, here are
                accredited online programs worth considering:
              </p>
            </div>

            <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={article.areaStudyId || 9} selectedProgram={mProgram} onProgramChange={onProgramChange} hideSidebar={true} data-id="element-1360" data-mp="3mo388rg1wl61" />
          </section>

          {/* Remainder of a rich body, when the listings were pulled up */}
          {trailingBlocks.length > 0 && <div className="mb-12" data-id="element-1361" data-mp="3aelz387o8a6v">
              {trailingBlocks.map((block, i) => renderBlock(block, splitAt + i))}
            </div>}

          {/* What to look for + conclusion (auto-generated body only) */}
          {!article.content && <>
              {area?.whatToLookFor && <section className="mb-12" data-id="element-1362" data-mp="3t4bpzk8bcbti">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1363" data-mp="3t30ndnqlta3z">
                    <SearchCheckIcon className="w-6 h-6 text-[#e8594f]" data-id="element-1364" data-mp="3p1ylez5ayjyb" />
                    What to Look for in a Program
                  </h2>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1365" data-mp="2hmtypk7x54nv">
                    {area.whatToLookFor}
                  </p>
                </section>}

              <section className="mb-12" data-id="element-1366" data-mp="2f5a1xb2a9rp0">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1367" data-mp="2k3jvjh8d0k8i">
                  The Bottom Line
                </h2>
                <p className="text-gray-700 leading-relaxed" data-id="element-1368" data-mp="257cegps8zyxg">
                  The right online program is the one that aligns with your
                  career goals, budget, and schedule. Use the comparison above
                  to explore accredited options and request information from the
                  schools that fit your needs.
                </p>
              </section>
            </>}
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && <section className="border-t border-gray-200 pt-10" data-id="element-1369" data-mp="1rg7ka37nfyui">
            <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1370" data-mp="1dfdbvkklpxvu">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-id="element-1371" data-mp="1hhi0g4w704bm">
              {relatedArticles.map(rel => <a key={rel.id} href={`/articles/${rel.slug}`} onClick={e => {
            e.preventDefault();
            onNavigate('articles', rel.slug);
          }} className="group bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow flex flex-col" data-id="element-1372" data-mp="ytpmnkzjk364">
                  <span className="text-[11px] font-semibold text-[#3b82f6] uppercase tracking-wide mb-2" data-id="element-1373" data-mp="1hqigbicmf60z">
                    {rel.category}
                  </span>
                  <h3 className="text-sm font-bold text-gray-900 leading-snug mb-3 line-clamp-2 group-hover:text-[#3b82f6] transition-colors" data-id="element-1374" data-mp="1qjucw09zfkf9">
                    {rel.title}
                  </h3>
                  <span className="mt-auto text-xs font-semibold text-[#e8594f] flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-1375" data-mp="20i1k3regyo42">
                    Read <ArrowRightIcon className="w-3 h-3" data-id="element-1376" data-mp="1vk9q8ru9krx8" />
                  </span>
                </a>)}
            </div>
          </section>}
      </div>
    </div>;
}